/**
 * Map Geometry Utilities
 * Helper functions for geometric calculations on Google Maps
 */

/**
 * Calculates orientation between two LatLng points
 * @returns Heading in degrees (0-360)
 */
export const calculateOrientation = (from: google.maps.LatLng, to: google.maps.LatLng): number => {
  return google.maps.geometry.spherical.computeHeading(from, to);
};

/**
 * Computes a point at a given distance and bearing from a center point
 */
export const computeOffset = (
  center: google.maps.LatLng,
  distance: number,
  heading: number
): google.maps.LatLng => {
  return google.maps.geometry.spherical.computeOffset(center, distance, heading);
};

/**
 * Computes the distance between two points in meters
 */
export const computeDistance = (from: google.maps.LatLng, to: google.maps.LatLng): number => {
  return google.maps.geometry.spherical.computeDistanceBetween(from, to);
};

/**
 * Computes the area
 */
export const computeArea = (path: google.maps.LatLng[]): number => {
  return google.maps.geometry.spherical.computeArea(path);
};

/**
 * Interpolates between two points at a given fraction
 * @param fraction - Value between 0 and 1 (0 = from, 1 = to, 0.5 = midpoint)
 */
export const interpolate = (
  from: google.maps.LatLng,
  to: google.maps.LatLng,
  fraction: number
): google.maps.LatLng => {
  return google.maps.geometry.spherical.interpolate(from, to, fraction);
};

/**
 * Sets orientation by computing two offset points from a center at a given orientation
 * Useful for orientation selector line endpoints
 */
export const setOrientationOffsets = (
  firstCenter: google.maps.LatLng,
  secondCenter: google.maps.LatLng,
  middle: google.maps.LatLng,
  orientation: number
): { offset1: google.maps.LatLng; offset2: google.maps.LatLng } => {
  const length = google.maps.geometry.spherical.computeLength([firstCenter, secondCenter]);

  const offset1 = google.maps.geometry.spherical.computeOffset(middle, -length / 2, orientation);

  const offset2 = google.maps.geometry.spherical.computeOffset(middle, length / 2, orientation);

  return { offset1, offset2 };
};

/**
 * Converts a position object to google.maps.LatLng
 */
export const positionToLatLng = (position: {
  latitude: number;
  longitude: number;
}): google.maps.LatLng => {
  return new google.maps.LatLng(position.latitude, position.longitude);
};

/**
 * Converts a google.maps.LatLng to a plain position object
 */
export const latLngToPosition = (
  latLng: google.maps.LatLng
): {
  latitude: number;
  longitude: number;
} => {
  return {
    latitude: latLng.lat(),
    longitude: latLng.lng(),
  };
};
